import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useComposer } from '../../context/NewMessageComposerContext';
import { authApi } from '../../services/authApi';
import { messageApi, normalizeSearchUser } from '../../services/messageApi';
import './NewMessageComposer.css';

/**
 * NewMessageComposer — Global floating compose panel.
 *
 * Mode A (search): User types a username to find recipients.
 * Mode B (compose): Shows recipient header + message input + cost hint.
 *
 * Renders fixed bottom-right at z-index 1000 and persists across routes.
 */

function NewMessageComposer() {
  const navigate = useNavigate();
  const {
    isOpen,
    mode,
    recipient,
    draftText,
    isExpanded,
    closeComposer,
    selectRecipient,
    backToSearch,
    setDraftText,
    toggleExpanded,
  } = useComposer();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isClosing, setIsClosing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const searchInputRef = useRef(null);
  const composeInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && mode === 'search' && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, mode]);

  // Focus compose input when switching to compose mode
  useEffect(() => {
    if (isOpen && mode === 'compose' && isExpanded && composeInputRef.current) {
      setTimeout(() => composeInputRef.current?.focus(), 100);
    }
  }, [isOpen, mode, isExpanded]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      authApi.searchUsers(searchQuery)
        .then((results) => {
          setSearchResults(results.map(normalizeSearchUser));
        })
        .catch(() => {
          setSearchResults([]);
        });
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (mode === 'compose') {
          toggleExpanded();
        } else {
          handleClose();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, mode, toggleExpanded]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeComposer();
      setIsClosing(false);
      setSearchQuery('');
      setSearchResults([]);
      setError('');
    }, 150);
  }, [closeComposer]);

  const handleSelectUser = useCallback((user) => {
    setError('');
    selectRecipient(user);
    setSearchQuery('');
    setSearchResults([]);
  }, [selectRecipient]);

  const handleSend = useCallback(() => {
    const send = async () => {
      const trimmed = draftText.trim();
      if (!trimmed || !recipient || isSending) return;

      setIsSending(true);
      setError('');
      try {
        const conversation = await messageApi.createConversation(recipient.username);
        await messageApi.sendMessage(conversation.conversationId, trimmed);
        setDraftText('');
        closeComposer();
        navigate(`/messages?conversation=${encodeURIComponent(conversation.conversationId)}`);
      } catch (sendError) {
        setError(sendError.message || 'Failed to send message');
      } finally {
        setIsSending(false);
      }
    };

    send();
  }, [closeComposer, draftText, isSending, navigate, recipient, setDraftText]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = useCallback(() => {
    backToSearch();
    setSearchQuery('');
  }, [backToSearch]);

  if (!isOpen && !isClosing) return null;

  return (
    <div className={`nmc ${isClosing ? 'nmc--closing' : ''}`}>
      {/* ── Mode A: User Search ────────────────────────────────────── */}
      {mode === 'search' && (
        <div className="nmc__panel nmc__panel--search">
          <div className="nmc__header">
            <span className="nmc__header-title">Start a New Message</span>
            <div className="nmc__header-actions">
              <button type="button" className="nmc__header-btn" onClick={toggleExpanded} aria-label="Minimize" title="Minimize">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 18L18 18" />
                </svg>
              </button>
              <button type="button" className="nmc__header-btn" onClick={handleClose} aria-label="Close" title="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="nmc__body">
              <div className="nmc__search-input-wrapper">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="nmc__search-input"
                  placeholder="Type a username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {searchQuery.trim() === '' ? (
                <p className="nmc__search-hint">Find someone by username to start a message</p>
              ) : searchResults.length === 0 ? (
                <p className="nmc__search-hint">No users found for "{searchQuery}"</p>
              ) : (
                <ul className="nmc__search-results">
                  {searchResults.map((user) => (
                    <li key={user.id} className="nmc__search-result">
                      <img src={user.avatar} alt="" className="nmc__result-avatar" />
                      <span className="nmc__result-name">{user.username}</span>
                      <button
                        type="button"
                        className="nmc__result-msg-btn"
                        onClick={() => handleSelectUser(user)}
                      >
                        Message
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Mode B: Compose ────────────────────────────────────────── */}
      {mode === 'compose' && recipient && (
        <div className="nmc__panel nmc__panel--compose">
          <div className="nmc__header nmc__header--compose">
            <button type="button" className="nmc__header-back" onClick={handleBack} aria-label="Back to search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
            </button>
            <img src={recipient.avatar} alt="" className="nmc__header-avatar" />
            <span className="nmc__header-name">{recipient.username}</span>
            <div className="nmc__header-actions">
              <button type="button" className="nmc__header-btn" onClick={toggleExpanded} aria-label={isExpanded ? 'Collapse' : 'Expand'} title={isExpanded ? 'Collapse' : 'Expand'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  {isExpanded ? <path d="M6 18L18 18" /> : <path d="M6 6L18 6" />}
                </svg>
              </button>
              <button type="button" className="nmc__header-btn" onClick={handleClose} aria-label="Close" title="Close (Esc)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="nmc__compose-body">
              <div className="nmc__compose-input-row">
                <textarea
                  ref={composeInputRef}
                  className="nmc__compose-input"
                  placeholder="Type your message..."
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                <button
                  type="button"
                  className="nmc__compose-send"
                  onClick={handleSend}
                  disabled={!draftText.trim() || isSending}
                  aria-label="Send"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13" />
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                  </svg>
                </button>
              </div>
              <p className="nmc__compose-hint">
                This message will place a 200 spade hold until the creator replies.
              </p>
              {error && <p className="nmc__compose-error">{error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NewMessageComposer;
